import { scopedApi } from '@/session/scopedClients';
import { issueAdminTargetCapability } from '@/session/adminTargetCapability';
import { GLOBAL_SCOPE, type RequestScope } from '@/session/scopedTransport';
import { invalidateOrganizationList } from '@/session/organizationList';
import type { EndpointResponse } from '@/types/endpoints';
import type { IntegrationAllowance } from '@/types/integration';
import type { Organization, OrganizationSetting } from '@/types/organization';
import { utils } from '@/utils';
import type { ReadRequestOptions, WriteRequestOptions } from './requestOptions';

const capability = issueAdminTargetCapability();

const requireTarget = (scope: RequestScope): void => {
  if (scope.kind !== 'organization') throw new Error('Administrator target requests require an organization scope');
};

export const resolveAdminTargetOrganization = async (
  { id }: { id: string | number },
  options: ReadRequestOptions
): Promise<EndpointResponse<Organization>> => {
  try {
    const response = await scopedApi(GLOBAL_SCOPE).get<EndpointResponse<Organization[]>>('organization', {
      ...options,
      params: { limit: 1, query: JSON.stringify({ id: Number(id) }) },
    });
    if (response.data.isError) return response.data;
    const organization = response.data.result?.find((candidate) => String(candidate.id) === String(id));
    if (!organization) throw new Error('Administrator target organization was not found');
    return { ...response.data, result: organization, num_results: 1 };
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const getAdminTargetOrganization = async (
  scope: RequestScope,
  { id }: { id: string | number },
  options: ReadRequestOptions
): Promise<EndpointResponse<Organization>> => {
  try {
    requireTarget(scope);
    const response = await scopedApi(scope).get<EndpointResponse<Organization>>(`organization/${id}`, {
      ...options,
      _sessionAdminTargetCapability: capability,
    });
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const getAdminTargetOrganizationSettings = async (
  scope: RequestScope,
  params: { limit?: number },
  options: ReadRequestOptions
): Promise<EndpointResponse<OrganizationSetting[]>> => {
  try {
    requireTarget(scope);
    const response = await scopedApi(scope).get<EndpointResponse<OrganizationSetting[]>>('organization/setting', {
      ...options,
      _sessionAdminTargetCapability: capability,
      params: { limit: params.limit ?? 1000 },
    });
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const getAdminTargetIntegrationAllowances = async (
  scope: RequestScope,
  params: { limit?: number; query?: unknown },
  options: ReadRequestOptions
): Promise<EndpointResponse<IntegrationAllowance[]>> => {
  try {
    requireTarget(scope);
    // Never `all`: an all-organizations read skips the API's per-row install count, so every `used` would read 0. The
    // target header already limits the read to the edited organization.
    const response = await scopedApi(scope).get<EndpointResponse<IntegrationAllowance[]>>('/integration_allowance', {
      ...options,
      _sessionAdminTargetCapability: capability,
      params: { limit: params.limit ?? 1000, query: params.query },
    });
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const updateAdminTargetOrganization = async (
  scope: RequestScope,
  { id, data }: { id: string | number; data: Partial<Organization> },
  options: WriteRequestOptions
): Promise<EndpointResponse<Organization>> => {
  try {
    requireTarget(scope);
    const response = await scopedApi(scope).put<EndpointResponse<Organization>>(
      `organization/${id}`,
      { ...data, name: scope.kind === 'organization' ? scope.organization : data.name },
      { ...options, _sessionAdminTargetCapability: capability }
    );
    if (!response.data.isError) invalidateOrganizationList();
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const createAdminTargetOrganizationSetting = async (
  scope: RequestScope,
  params: { data: { name: string; value: string; organization_name?: string } },
  options: WriteRequestOptions
): Promise<EndpointResponse<OrganizationSetting>> => {
  try {
    requireTarget(scope);
    const response = await scopedApi(scope).post<EndpointResponse<OrganizationSetting>>(
      'organization/setting',
      { ...params.data, organization_name: scope.kind === 'organization' ? scope.organization : params.data.organization_name },
      { ...options, _sessionAdminTargetCapability: capability }
    );
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const updateAdminTargetOrganizationSetting = async (
  scope: RequestScope,
  { id, data }: { id: string | number; data: { name: string; value: string } },
  options: WriteRequestOptions
): Promise<EndpointResponse<OrganizationSetting>> => {
  try {
    requireTarget(scope);
    const response = await scopedApi(scope).patch<EndpointResponse<OrganizationSetting>>(`organization/setting/${id}`, data, {
      ...options,
      _sessionAdminTargetCapability: capability,
    });
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};

export const deleteAdminTargetOrganizationSetting = async (
  scope: RequestScope,
  { id }: { id: string | number },
  options: WriteRequestOptions
): Promise<EndpointResponse<Record<string, never>>> => {
  try {
    requireTarget(scope);
    const response = await scopedApi(scope).delete<EndpointResponse<Record<string, never>>>(`organization/setting/${id}`, {
      ...options,
      _sessionAdminTargetCapability: capability,
    });
    return response.data;
  } catch (error) {
    return utils.error.handleAxiosError(error);
  }
};
